/**
 * Automatische Priorisierung der To-Do-Seite (Paul 02.11.2026).
 *
 * Feste, erklärbare Regeln statt KI: jeder Punkt bekommt eine Stufe und —
 * wenn er deshalb oben steht — einen sichtbaren Grund als Schild.
 *   1 = sofort (Notfall, „sofort zurückrufen“)
 *   2 = heute  (heute zurückrufen, mehrfach angerufen, Beschwerde, Mail älter
 *               als 1 Werktag, Aufgabe überfällig/fällig heute/hoch)
 *   3 = normal (alles andere, auch Vorgänge ohne Termin)
 * Wer länger als 2 Werktage wartet, rutscht von 3 auf 2 — so bleibt nichts
 * dauerhaft unten liegen. Stufe 1 gibt es nur aus echtem Grund: auf dev
 * standen sonst fünf Tage alte Mails vor „sofort zurückrufen“.
 */

export type Stufe = 1 | 2 | 3

export type Prio = {
  stufe: Stufe
  /** Schild-Text; null = kein eigener Grund (Wartezeit-Schild reicht). */
  grund: string | null
  /** Fertiger Vorschlag, ein Klick — innerhalb der Stufe zuerst. */
  schnell?: boolean
}

export const NORMAL: Prio = { stufe: 3, grund: null }

/** Dieselbe Rechnung wie `services.mail_offen.werktag_grenze` (Sa/So zählen nicht). */
export function werktagGrenze(werktage: number, jetzt = new Date()): Date {
  const t = new Date(jetzt)
  let rest = werktage
  while (rest > 0) {
    t.setDate(t.getDate() - 1)
    const tag = t.getDay()
    if (tag !== 0 && tag !== 6) rest -= 1
  }
  return t
}

export function aelterAlsWerktage(iso: string | null | undefined, werktage: number, jetzt = new Date()): boolean {
  if (!iso) return false
  const t = new Date(iso).getTime()
  return !Number.isNaN(t) && t < werktagGrenze(werktage, jetzt).getTime()
}

/** KI-Telefon-Anruf: Dringlichkeit des Bots, Notfall, Beschwerde. */
export function prioAnruf(c: {
  flag_callback_urgency?: string | null; intent?: string | null
}, zustandKey: string): Prio {
  if (c.intent === "emergency") return { stufe: 1, grund: "Notfall" }
  if (zustandKey === "rueckruf" && c.flag_callback_urgency === "now") return { stufe: 1, grund: "sofort zurückrufen" }
  if (zustandKey === "rueckruf" && c.flag_callback_urgency === "today") return { stufe: 2, grund: "heute zurückrufen" }
  if (zustandKey === "stumm") return { stufe: 2, grund: "Bot verstummt" }
  if (c.intent === "complaint") return { stufe: 2, grund: "Beschwerde" }
  return NORMAL
}

/** Verpasster Team-Anruf: wer mehrmals anruft, will dringend etwas. */
export function prioVerpasst(anzahl: number): Prio {
  return anzahl >= 2 ? { stufe: 2, grund: `${anzahl}× angerufen` } : NORMAL
}

const BESCHWERDE = /beschwerde|reklamation|mangel|mängel/i

/** Mail: Beschwerde, oder länger als einen Werktag offen. Ohne Schild — das
 *  Wartezeit-Schild sagt es schon, und „Kunde wartet“ stand auf dev auch an
 *  einer Uber-Eats-Rechnung (der Reiter prüft den Absender, diese Regel nicht). */
export function prioMail(m: { category?: string | null; received_at?: string | null; topf?: string }, jetzt = new Date()): Prio {
  const schnell = m.topf === "klick"
  if (BESCHWERDE.test(m.category || "")) return { stufe: 2, grund: "Beschwerde", schnell }
  if (aelterAlsWerktage(m.received_at, 1, jetzt)) return { stufe: 2, grund: null, schnell }
  return { ...NORMAL, schnell }
}

/** Interne Aufgabe: Fälligkeit steht schon im Wartezeit-Schild, nur „hoch“ bekommt ein eigenes. */
export function prioAufgabe(a: { faellig_am?: string | null; prioritaet?: string | null }, heute: string): Prio {
  if (a.faellig_am && a.faellig_am <= heute) return { stufe: 2, grund: a.prioritaet === "hoch" ? "wichtig" : null }
  if (a.prioritaet === "hoch") return { stufe: 2, grund: "wichtig" }
  return NORMAL
}

/** Lange gewartet → von „normal“ auf „heute“ (nie auf „sofort“). */
export function mitAlter(p: Prio, seit: string | null | undefined, jetzt = new Date()): Prio {
  if (p.stufe === 3 && aelterAlsWerktage(seit, 2, jetzt)) return { ...p, stufe: 2 }
  return p
}

/** Sortierung: Stufe, dann mit Grund (Rückruf, Beschwerde …) vor bloßem
 *  Warten, dann Ein-Klick-Vorschläge, dann wer am längsten wartet. */
export function vergleich<T extends { prio: Prio; zeit?: string | null }>(a: T, b: T): number {
  if (a.prio.stufe !== b.prio.stufe) return a.prio.stufe - b.prio.stufe
  if (!!a.prio.grund !== !!b.prio.grund) return a.prio.grund ? -1 : 1
  if (!!a.prio.schnell !== !!b.prio.schnell) return a.prio.schnell ? -1 : 1
  // Ohne Zeit (Aufgaben) bleibt die Reihenfolge der Quelle — sort ist stabil.
  if (!a.zeit || !b.zeit) return a.zeit ? -1 : b.zeit ? 1 : 0
  return a.zeit.localeCompare(b.zeit)
}

export const ZUERST_MAX = 3

/** Die wichtigsten Punkte über alle Quellen — nur Stufe 1 und 2, sonst kein Block. */
export function zuerst<T extends { prio: Prio; zeit?: string | null }>(alle: T[]): T[] {
  return alle.filter((e) => e.prio.stufe <= 2).sort(vergleich).slice(0, ZUERST_MAX)
}
